import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Avatar,
  IconButton,
  LinearProgress,
  Alert,
  Fade,
  CircularProgress
} from '@mui/material';
import {
  PhotoCamera,
  EmojiEvents,
  CheckCircle,
  Cancel,
  Person
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';

const UsernameModal = ({ open, onClose, onUsernameSet }) => {
  const { session } = useAuth();
  const [username, setUsername] = useState('');
  const [avatar, setAvatar] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef(null);

  const checkUsernameAvailability = async (usernameToCheck) => {
    if (!usernameToCheck || usernameToCheck.length < 3) {
      setUsernameAvailable(null);
      return;
    }

    setCheckingUsername(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/leaderboard/check-username`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ username: usernameToCheck })
      });

      const data = await response.json();
      setUsernameAvailable(data.available);
      if (!data.available) {
        setError(data.message);
      } else {
        setError('');
        setSuccess(data.message);
      }
    } catch (error) {
      console.error('Error checking username:', error);
      setError('Failed to check username availability');
    } finally {
      setCheckingUsername(false);
    }
  };

  const handleUsernameChange = (e) => {
    const newUsername = e.target.value;
    setUsername(newUsername);
    setError('');
    setSuccess('');
    
    // Debounce username checking
    const timeoutId = setTimeout(() => {
      checkUsernameAvailability(newUsername);
    }, 500);

    return () => clearTimeout(timeoutId);
  };

  const handleAvatarSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('Avatar file must be smaller than 5MB');
        return;
      }

      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }

      setAvatar(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatarPreview(e.target.result);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const handleSubmit = async () => {
    if (!username) {
      setError('Please enter a username');
      return;
    }

    if (usernameAvailable !== true) {
      setError('Please choose an available username');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // First, upload avatar if selected
      let avatarUrl = null;
      if (avatar) {
        console.log('Uploading avatar:', { name: avatar.name, size: avatar.size, type: avatar.type });
        
        const formData = new FormData();
        formData.append('avatar', avatar);

        const avatarResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/leaderboard/upload-avatar`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          },
          body: formData
        });

        if (avatarResponse.ok) {
          const avatarData = await avatarResponse.json();
          avatarUrl = avatarData.avatar_url;
          console.log('Avatar uploaded successfully:', avatarUrl);
        } else {
          const errorData = await avatarResponse.json();
          console.error('Avatar upload failed:', errorData);
          setError(`Avatar upload failed: ${errorData.error}`);
          return;
        }
      }

      // Set username
      const usernameResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/leaderboard/set-username`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ username })
      });

      if (usernameResponse.ok) {
        setSuccess('Username set successfully! Welcome to the leaderboard! 🎉');
        setTimeout(() => {
          onUsernameSet();
        }, 1500);
      } else {
        const errorData = await usernameResponse.json();
        setError(errorData.error || 'Failed to set username');
      }
    } catch (error) {
      console.error('Error setting username:', error);
      setError('Failed to set username. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getAvatarDisplay = () => {
    if (avatarPreview) {
      return <Avatar src={avatarPreview} sx={{ width: 100, height: 100 }} />;
    }
    
    if (username) {
      const colors = [
        '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
        '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'
      ];
      const color = colors[username.charCodeAt(0) % colors.length];
      
      return (
        <Avatar sx={{
          width: 100,
          height: 100,
          bgcolor: color,
          color: 'white',
          fontSize: '2rem',
          fontWeight: 'bold'
        }}>
          {username.charAt(0).toUpperCase()}
        </Avatar>
      );
    }

    return (
      <Avatar sx={{
        width: 100,
        height: 100,
        bgcolor: 'grey.300',
        color: 'grey.600'
      }}>
        <Person sx={{ fontSize: 50 }} />
      </Avatar>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={!loading ? onClose : undefined}
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1, color: 'white' }}>
        <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={1}>
          <EmojiEvents sx={{ color: '#FFD700', fontSize: 32 }} />
          <Typography variant="h5" fontWeight="700">
            Join the Leaderboard!
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
          Enter a unique username to get listed on the leaderboard
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {loading && (
          <Box sx={{ width: '100%', mb: 2 }}>
            <LinearProgress 
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#FFD700'
                }
              }} 
            />
          </Box>
        )}

        <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
          {/* Avatar Section */}
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              Choose Your Avatar
            </Typography>
            
            <Box position="relative">
              {getAvatarDisplay()}
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
                sx={{
                  position: 'absolute',
                  bottom: -8,
                  right: -8,
                  bgcolor: '#FFD700',
                  color: 'white',
                  '&:hover': { bgcolor: '#FFA000' },
                  '&:disabled': { bgcolor: 'grey.400' }
                }}
                size="small"
              >
                <PhotoCamera />
              </IconButton>
            </Box>
            
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleAvatarSelect}
              accept="image/*"
              style={{ display: 'none' }}
            />
            
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', textAlign: 'center' }}>
              Upload a profile picture or we'll generate one based on your username
            </Typography>
          </Box>

          {/* Username Section */}
          <Box width="100%">
            <Typography variant="h6" gutterBottom sx={{ color: 'rgba(255,255,255,0.9)' }}>
              Enter Your Username
            </Typography>
            
            <TextField
              fullWidth
              value={username}
              onChange={handleUsernameChange}
              placeholder="Enter a unique username..."
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255,255,255,0.9)',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                  '&:hover fieldset': { borderColor: 'white' },
                  '&.Mui-focused fieldset': { borderColor: '#FFD700' }
                }
              }}
              InputProps={{
                endAdornment: checkingUsername ? (
                  <CircularProgress size={20} />
                ) : usernameAvailable === true ? (
                  <CheckCircle sx={{ color: 'green' }} />
                ) : usernameAvailable === false ? (
                  <Cancel sx={{ color: 'red' }} />
                ) : null
              }}
            />
            
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', mt: 1, display: 'block' }}>
              3-50 characters, letters, numbers, and underscores only
            </Typography>
          </Box>

          {/* Status Messages */}
          {error && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Fade in>
              <Alert severity="success" sx={{ width: '100%' }}>
                {success}
              </Alert>
            </Fade>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{ 
            color: 'rgba(255,255,255,0.7)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
          }}
        >
          Maybe Later
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading || !username || usernameAvailable !== true}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <EmojiEvents />}
          sx={{
            bgcolor: '#FFD700',
            color: 'black',
            fontWeight: 'bold',
            '&:hover': { bgcolor: '#FFA000' },
            '&:disabled': { bgcolor: 'grey.400', color: 'grey.600' }
          }}
        >
          {loading ? 'Setting up...' : 'Join Leaderboard!'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UsernameModal;
